import { useState, useEffect } from 'react'
import {
  Building2, Bell, Palette, Shield, Link2,
  Check, Moon, Sun, Monitor, Save, Loader2,
} from 'lucide-react'
import { useToast } from '../context/ToastContext'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'

// ── Shared UI components ───────────────────────────────────────
function Section({ title, desc, children }: { title: string; desc: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,198,0,0.1)' }}>
      <div className="px-6 py-4 border-b" style={{ background: '#0f0f0f', borderColor: 'rgba(255,198,0,0.08)' }}>
        <h3 className="font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{title}</h3>
        <p className="text-xs mt-0.5" style={{ color: '#555' }}>{desc}</p>
      </div>
      <div className="p-6 space-y-5" style={{ background: '#000' }}>{children}</div>
    </div>
  )
}

function FieldRow({ label, desc, children }: { label: string; desc?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-6">
      <div className="flex-shrink-0 w-48">
        <p className="text-sm font-medium text-white">{label}</p>
        {desc && <p className="text-xs mt-0.5" style={{ color: '#555' }}>{desc}</p>}
      </div>
      <div className="flex-1 max-w-sm">{children}</div>
    </div>
  )
}

function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button onClick={() => onChange(!value)}
      className="relative flex-shrink-0 w-11 h-6 rounded-full transition-all duration-200"
      style={{ background: value ? '#ffc600' : '#2a2a2a' }}>
      <span className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full transition-all duration-200 shadow-sm"
        style={{ background: '#fff', transform: value ? 'translateX(20px)' : 'translateX(0)' }} />
    </button>
  )
}

function SaveBtn({ onClick, saving, label = 'Save Changes' }: { onClick: () => void; saving?: boolean; label?: string }) {
  return (
    <button onClick={onClick} disabled={saving}
      className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all"
      style={{ background: saving ? 'rgba(255,198,0,0.5)' : '#ffc600', color: '#000' }}
      onMouseEnter={e => { if (!saving) e.currentTarget.style.background = '#e6b200' }}
      onMouseLeave={e => { if (!saving) e.currentTarget.style.background = '#ffc600' }}>
      {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
      {saving ? 'Saving…' : label}
    </button>
  )
}

const iCls   = "w-full rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-yellow-400/50 transition-all"
const iStyle = { background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.15)' }

// ── Company Profile ────────────────────────────────────────────
function CompanyTab() {
  const { toast } = useToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    name:     'Clickbait Marketing',
    tagline:  'Performance Marketing Agency',
    email:    'hello@clickbait.media',
    phone:    '+91 98200 00000',
    website:  'clickbait.media',
    address:  'Bengaluru, Karnataka, India',
    gstin:    '29AADCK1234A1Z5',
    currency: 'INR',
    timezone: 'Asia/Kolkata',
  })

  useEffect(() => {
    supabase.from('app_settings').select('*').eq('id', 1).single()
      .then(({ data, error }) => {
        if (error) {
          console.warn('[settings] app_settings load:', error.message)
          return
        }
        if (data) setForm({
          name:     data.company_name || '',
          tagline:  data.tagline      || '',
          email:    data.email        || '',
          phone:    data.phone        || '',
          website:  data.website      || '',
          address:  data.address      || '',
          gstin:    data.gstin        || '',
          currency: data.currency     || 'INR',
          timezone: data.timezone     || 'Asia/Kolkata',
        })
      })
  }, [])

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))

  const handleSave = async () => {
    setSaving(true)
    try {
      const { error } = await supabase.from('app_settings').upsert({
        id:           1,
        company_name: form.name.trim(),
        tagline:      form.tagline.trim(),
        email:        form.email.trim(),
        phone:        form.phone.trim(),
        website:      form.website.trim(),
        address:      form.address.trim(),
        gstin:        form.gstin.trim(),
        currency:     form.currency,
        timezone:     form.timezone,
        updated_at:   new Date().toISOString(),
      }, { onConflict: 'id' })

      if (error) {
        // app_settings table doesn't exist yet
        if (error.message?.includes('relation') || error.message?.includes('does not exist')) {
          toast('Run the app_settings migration in Supabase SQL Editor first', 'error')
        } else {
          toast(error.message || 'Failed to save', 'error')
        }
        return
      }
      toast('Company settings saved ✓', 'success')
    } catch (e: any) {
      toast(e.message || 'Failed to save', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Section title="Company Profile" desc="Basic information about your agency — used on invoices and PDFs">
      <FieldRow label="Agency Name">
        <input value={form.name} onChange={e => set('name', e.target.value)}
          className={iCls} style={iStyle} placeholder="Agency name" />
      </FieldRow>
      <FieldRow label="Tagline">
        <input value={form.tagline} onChange={e => set('tagline', e.target.value)}
          className={iCls} style={iStyle} placeholder="One-liner description" />
      </FieldRow>
      <FieldRow label="Contact Email">
        <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
          className={iCls} style={iStyle} placeholder="billing@agency.com" />
      </FieldRow>
      <FieldRow label="Phone">
        <input value={form.phone} onChange={e => set('phone', e.target.value)}
          className={iCls} style={iStyle} />
      </FieldRow>
      <FieldRow label="Website">
        <input value={form.website} onChange={e => set('website', e.target.value)}
          className={iCls} style={iStyle} />
      </FieldRow>
      <FieldRow label="Address">
        <input value={form.address} onChange={e => set('address', e.target.value)}
          className={iCls} style={iStyle} />
      </FieldRow>
      <FieldRow label="GSTIN" desc="For Indian GST invoicing">
        <input value={form.gstin} onChange={e => set('gstin', e.target.value)}
          className={iCls} style={iStyle} placeholder="29XXXXX0000X1Z5" />
      </FieldRow>
      <FieldRow label="Currency">
        <select value={form.currency} onChange={e => set('currency', e.target.value)}
          className={iCls} style={iStyle}>
          <option value="INR">INR — Indian Rupee (₹)</option>
          <option value="USD">USD — US Dollar ($)</option>
          <option value="EUR">EUR — Euro (€)</option>
          <option value="GBP">GBP — British Pound (£)</option>
        </select>
      </FieldRow>
      <FieldRow label="Timezone">
        <select value={form.timezone} onChange={e => set('timezone', e.target.value)}
          className={iCls} style={iStyle}>
          <option value="Asia/Kolkata">Asia/Kolkata (IST, UTC+5:30)</option>
          <option value="Asia/Dubai">Asia/Dubai (GST, UTC+4)</option>
          <option value="UTC">UTC</option>
          <option value="America/New_York">America/New_York (ET)</option>
          <option value="Europe/London">Europe/London (GMT/BST)</option>
        </select>
      </FieldRow>
      <div className="pt-2">
        <SaveBtn onClick={handleSave} saving={saving} />
      </div>
    </Section>
  )
}

// ── Notifications ──────────────────────────────────────────────
const NOTIF_KEY = 'erp_notif_prefs'

const NOTIF_DEFAULTS = {
  emailInvoicePaid:       true,
  emailNewClient:         true,
  emailProjectDone:       false,
  emailWeeklyReport:      true,
  browserNewDeal:         true,
  browserTaskDue:         true,
  browserCalendarReminder: true,
  browserOverdueInvoice:  true,
  slackChannel:           '#general',
  slackEnabled:           false,
}

function NotificationsTab() {
  const { toast } = useToast()
  const [notifs, setNotifs] = useState(NOTIF_DEFAULTS)

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(NOTIF_KEY)
      if (saved) setNotifs(JSON.parse(saved))
    } catch {}
  }, [])

  const toggle = (key: keyof typeof notifs) =>
    setNotifs(f => ({ ...f, [key]: !f[key] }))

  const handleSave = () => {
    localStorage.setItem(NOTIF_KEY, JSON.stringify(notifs))
    toast('Notification preferences saved ✓', 'success')
  }

  return (
    <div className="space-y-5">
      <Section title="Email Notifications" desc="Receive email alerts for important events">
        <FieldRow label="Invoice Paid" desc="When a client pays an invoice">
          <Toggle value={notifs.emailInvoicePaid} onChange={() => toggle('emailInvoicePaid')} />
        </FieldRow>
        <FieldRow label="New Client" desc="When a new client is added">
          <Toggle value={notifs.emailNewClient} onChange={() => toggle('emailNewClient')} />
        </FieldRow>
        <FieldRow label="Project Completed" desc="When a project is marked done">
          <Toggle value={notifs.emailProjectDone} onChange={() => toggle('emailProjectDone')} />
        </FieldRow>
        <FieldRow label="Weekly Report" desc="Summary every Monday morning">
          <Toggle value={notifs.emailWeeklyReport} onChange={() => toggle('emailWeeklyReport')} />
        </FieldRow>
      </Section>

      <Section title="Browser Notifications" desc="In-app push notifications while using Clickbait">
        <FieldRow label="New Deal" desc="When a deal moves to a new stage">
          <Toggle value={notifs.browserNewDeal} onChange={() => toggle('browserNewDeal')} />
        </FieldRow>
        <FieldRow label="Task Due" desc="24 hours before a task deadline">
          <Toggle value={notifs.browserTaskDue} onChange={() => toggle('browserTaskDue')} />
        </FieldRow>
        <FieldRow label="Calendar Reminders" desc="Event alerts (30 min, 5 min, at time)">
          <Toggle value={notifs.browserCalendarReminder} onChange={() => toggle('browserCalendarReminder')} />
        </FieldRow>
        <FieldRow label="Overdue Invoices" desc="When invoices pass their due date">
          <Toggle value={notifs.browserOverdueInvoice} onChange={() => toggle('browserOverdueInvoice')} />
        </FieldRow>
      </Section>

      <Section title="Slack Integration" desc="Push notifications to a Slack channel">
        <FieldRow label="Enable Slack" desc="Send ERP events to Slack">
          <Toggle value={notifs.slackEnabled} onChange={() => toggle('slackEnabled')} />
        </FieldRow>
        {notifs.slackEnabled && (
          <FieldRow label="Channel" desc="Slack channel name">
            <input value={notifs.slackChannel}
              onChange={e => setNotifs(f => ({ ...f, slackChannel: e.target.value }))}
              className={iCls} style={iStyle} placeholder="#clickbait-alerts" />
          </FieldRow>
        )}
        <div className="pt-2">
          <SaveBtn onClick={handleSave} label="Save Preferences" />
        </div>
      </Section>
    </div>
  )
}

// ── Integrations ───────────────────────────────────────────────
function IntegrationsTab() {
  const integrations = [
    { id: 'google-calendar', name: 'Google Calendar', desc: 'Sync ERP events and get reminders in real-time',          icon: '📅', status: 'connected' as const, detail: 'Connected via OAuth 2.0' },
    { id: 'google-ads',      name: 'Google Ads',       desc: 'Import campaign performance and spend data',              icon: '📊', status: 'available' as const, detail: 'Connect via Google OAuth' },
    { id: 'meta-ads',        name: 'Meta Ads Manager', desc: 'Pull Facebook & Instagram ad metrics',                   icon: '📱', status: 'available' as const, detail: 'Connect via Meta App' },
    { id: 'razorpay',        name: 'Razorpay',         desc: 'Auto-mark invoices paid from payment gateway',           icon: '💳', status: 'available' as const, detail: 'Requires API key' },
    { id: 'slack',           name: 'Slack',            desc: 'Send ERP notifications to Slack channels',               icon: '💬', status: 'available' as const, detail: 'OAuth 2.0 app install' },
    { id: 'semrush',         name: 'Semrush',          desc: 'Pull SEO keyword rankings and audit data',               icon: '🔍', status: 'available' as const, detail: 'API key required' },
  ]

  return (
    <Section title="Integrations" desc="Connect Clickbait with your existing tools">
      <div className="space-y-3">
        {integrations.map(int => (
          <div key={int.id} className="flex items-center gap-4 p-4 rounded-xl transition-all"
            style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.08)' }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(255,198,0,0.2)')}
            onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,198,0,0.08)')}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
              style={{ background: '#1a1a1a' }}>{int.icon}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">{int.name}</p>
              <p className="text-xs mt-0.5 truncate" style={{ color: '#555' }}>{int.desc}</p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="text-xs" style={{ color: int.status === 'connected' ? '#32c864' : '#555' }}>
                {int.detail}
              </span>
              {int.status === 'connected' ? (
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold"
                  style={{ background: 'rgba(50,200,100,0.1)', color: '#32c864' }}>
                  <Check size={11} /> Connected
                </span>
              ) : (
                <button className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                  style={{ background: 'rgba(255,198,0,0.1)', color: '#ffc600', border: '1px solid rgba(255,198,0,0.2)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,198,0,0.2)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,198,0,0.1)')}>
                  Connect
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </Section>
  )
}

// ── Appearance ─────────────────────────────────────────────────
const APPEAR_KEY = 'erp_appearance_prefs'

const APPEAR_DEFAULTS = {
  theme:  'dark' as 'dark' | 'light' | 'system',
  accent: '#ffc600',
  dense:  false,
  font:   'DM Sans',
}

function AppearanceTab() {
  const { toast } = useToast()
  const [prefs, setPrefs] = useState(APPEAR_DEFAULTS)

  useEffect(() => {
    try {
      const saved = localStorage.getItem(APPEAR_KEY)
      if (saved) setPrefs(JSON.parse(saved))
    } catch {}
  }, [])

  const set = (k: string, v: unknown) => setPrefs(f => ({ ...f, [k]: v }))

  const handleSave = () => {
    localStorage.setItem(APPEAR_KEY, JSON.stringify(prefs))
    toast('Appearance preferences saved ✓', 'success')
  }

  const accents = ['#ffc600', '#32c864', '#7c7cff', '#ff8c00', '#cc3300', '#00bcd4']

  return (
    <Section title="Appearance" desc="Customise how Clickbait looks for you">
      <FieldRow label="Theme" desc="Interface colour scheme">
        <div className="flex gap-2">
          {([
            { id: 'dark',   label: 'Dark',   icon: Moon    },
            { id: 'light',  label: 'Light',  icon: Sun     },
            { id: 'system', label: 'System', icon: Monitor },
          ] as const).map(t => (
            <button key={t.id} onClick={() => set('theme', t.id)}
              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-semibold transition-all"
              style={prefs.theme === t.id
                ? { background: '#ffc600', color: '#000' }
                : { background: '#0f0f0f', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.1)' }}>
              <t.icon size={14} />{t.label}
            </button>
          ))}
        </div>
      </FieldRow>

      <FieldRow label="Accent Colour" desc="Primary highlight colour">
        <div className="flex gap-2 flex-wrap">
          {accents.map(c => (
            <button key={c} onClick={() => set('accent', c)}
              className="w-8 h-8 rounded-lg transition-all"
              style={{ background: c, outline: prefs.accent === c ? `2px solid ${c}` : 'none', outlineOffset: 2, opacity: prefs.accent === c ? 1 : 0.6 }}>
              {prefs.accent === c && <Check size={14} style={{ margin: 'auto', color: '#000' }} />}
            </button>
          ))}
        </div>
      </FieldRow>

      <FieldRow label="Compact Mode" desc="Reduce spacing for denser layout">
        <Toggle value={prefs.dense} onChange={v => set('dense', v)} />
      </FieldRow>

      <FieldRow label="Interface Font">
        <select value={prefs.font} onChange={e => set('font', e.target.value)} className={iCls} style={iStyle}>
          <option value="DM Sans">DM Sans (default)</option>
          <option value="Inter">Inter</option>
          <option value="Geist">Geist</option>
          <option value="Space Grotesk">Space Grotesk</option>
        </select>
      </FieldRow>

      <div className="pt-2">
        <SaveBtn onClick={handleSave} label="Apply Changes" />
      </div>
    </Section>
  )
}

// ── Security ───────────────────────────────────────────────────
function SecurityTab() {
  const { toast }       = useToast()
  const { currentUser } = useAuth()
  const [pw, setPw]     = useState({ current: '', next: '', confirm: '' })
  const [saving, setSaving] = useState(false)
  const [twoFa, setTwoFa]   = useState(false)

  const handlePasswordChange = async () => {
    if (!pw.current)              { toast('Enter your current password', 'error'); return }
    if (pw.next.length < 8)       { toast('New password must be at least 8 characters', 'error'); return }
    if (pw.next !== pw.confirm)   { toast('Passwords do not match', 'error'); return }
    if (pw.next === pw.current)   { toast('New password must be different', 'error'); return }

    setSaving(true)
    try {
      // Reauthenticate first to verify current password
      const { error: reAuthErr } = await supabase.auth.signInWithPassword({
        email:    currentUser?.email ?? '',
        password: pw.current,
      })
      if (reAuthErr) {
        toast('Current password is incorrect', 'error')
        return
      }

      // Now update to new password
      const { error } = await supabase.auth.updateUser({ password: pw.next })
      if (error) { toast(error.message, 'error'); return }

      setPw({ current: '', next: '', confirm: '' })
      toast('Password updated ✓', 'success')
    } catch (e: any) {
      toast(e.message || 'Failed to update password', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-5">
      <Section title="Change Password" desc="Update your login credentials">
        <FieldRow label="Current Password">
          <input type="password" value={pw.current}
            onChange={e => setPw(f => ({ ...f, current: e.target.value }))}
            className={iCls} style={iStyle} placeholder="••••••••" />
        </FieldRow>
        <FieldRow label="New Password">
          <input type="password" value={pw.next}
            onChange={e => setPw(f => ({ ...f, next: e.target.value }))}
            className={iCls} style={iStyle} placeholder="Min. 8 characters" />
        </FieldRow>
        <FieldRow label="Confirm New Password">
          <input type="password" value={pw.confirm}
            onChange={e => setPw(f => ({ ...f, confirm: e.target.value }))}
            className={iCls} style={iStyle} placeholder="••••••••" />
        </FieldRow>
        <div className="pt-2">
          <SaveBtn onClick={handlePasswordChange} saving={saving} label="Update Password" />
        </div>
      </Section>

      <Section title="Security Options" desc="Additional account protection settings">
        <FieldRow label="Two-Factor Auth" desc="Require TOTP on every sign-in (coming soon)">
          <Toggle value={twoFa} onChange={setTwoFa} />
        </FieldRow>
      </Section>
    </div>
  )
}

// ── Main Page ──────────────────────────────────────────────────
const TABS = [
  { id: 'company',       label: 'Company',       icon: Building2 },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'integrations',  label: 'Integrations',  icon: Link2 },
  { id: 'appearance',    label: 'Appearance',    icon: Palette },
  { id: 'security',      label: 'Security',      icon: Shield },
] as const

type TabId = typeof TABS[number]['id']

export default function Settings() {
  const [tab, setTab] = useState<TabId>('company')

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Settings</h1>
        <p className="text-sm mt-0.5" style={{ color: '#555' }}>Manage your agency, preferences, and integrations</p>
      </div>

      <div className="flex gap-6">
        {/* Sidebar */}
        <aside className="w-48 flex-shrink-0">
          <nav className="space-y-0.5">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-left"
                style={tab === t.id
                  ? { background: 'rgba(255,198,0,0.1)', color: '#ffc600', borderLeft: '2px solid #ffc600' }
                  : { color: '#a0a0a0', borderLeft: '2px solid transparent' }}
                onMouseEnter={e => { if (tab !== t.id) e.currentTarget.style.color = '#fff' }}
                onMouseLeave={e => { if (tab !== t.id) e.currentTarget.style.color = '#a0a0a0' }}>
                <t.icon size={16} />{t.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {tab === 'company'       && <CompanyTab />}
          {tab === 'notifications' && <NotificationsTab />}
          {tab === 'integrations'  && <IntegrationsTab />}
          {tab === 'appearance'    && <AppearanceTab />}
          {tab === 'security'      && <SecurityTab />}
        </div>
      </div>
    </div>
  )
}
